import maplibregl from 'maplibre-gl';
import GeoCoderAPIs from './GeoCoderAPIs.js';

/* ============================================================================
 * Valhalla helpers
 * ==========================================================================*/

/**
 * Decode a Valhalla-encoded polyline string into an array of [lng, lat] pairs.
 *
 * IMPORTANT: Valhalla uses 6 digits of precision (1e6), NOT the Google-standard
 * 5 (1e5). Decoding with the wrong precision drops your route into the ocean.
 * Output is [lng, lat] order so it can be used directly as GeoJSON coordinates.
 *
 * @param {string} encoded - the encoded shape string
 * @param {number} precision - decimal precision (default 6 for Valhalla)
 * @returns {number[][]} array of [lng, lat] coordinates
 */
export function decodeValhallaShape(encoded, precision = 6) {
  if (!encoded) return [];

  const factor = Math.pow(10, precision);
  let index = 0;
  let lat = 0;
  let lng = 0;
  const coordinates = [];

  while (index < encoded.length) {
    let result = 0;
    let shift = 0;
    let byte;

    // latitude
    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);
    lat += result & 1 ? ~(result >> 1) : result >> 1;

    // longitude
    result = 0;
    shift = 0;
    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);
    lng += result & 1 ? ~(result >> 1) : result >> 1;

    // GeoJSON wants [lng, lat]
    coordinates.push([lng / factor, lat / factor]);
  }

  return coordinates;
}

/**
 * Convert a Valhalla `route` / `optimized_route` response into a GeoJSON
 * FeatureCollection.
 *
 * Valhalla returns one encoded `shape` per leg under `trip.legs[]`. We decode
 * each leg and concatenate them into a single LineString so the whole trip
 * renders as one continuous line. The trip summary is attached as feature
 * properties for convenience (length in km, time in seconds).
 *
 * @param {object} response - parsed Valhalla JSON response
 * @returns {object} GeoJSON FeatureCollection
 */
export function valhallaRouteToGeoJSON(response) {
  const trip = response && response.trip;
  const legs = (trip && trip.legs) || [];

  let coordinates = [];
  legs.forEach((leg) => {
    const legCoords = decodeValhallaShape(leg.shape, 6);
    if (coordinates.length && legCoords.length) {
      // avoid duplicating the shared vertex between consecutive legs
      legCoords.shift();
    }
    coordinates.push(...legCoords);
  });

  // Drop any non-finite coordinate pairs. A single NaN/Infinity in the array
  // makes MapLibre's GeoJSON worker throw an opaque error on a later tile parse.
  coordinates = coordinates.filter(
    (c) => Array.isArray(c) && Number.isFinite(c[0]) && Number.isFinite(c[1])
  );

  const features = [];
  // A LineString needs at least 2 positions to be valid GeoJSON. Emitting an
  // empty/1-point LineString can corrupt the source and surface as
  // "Unimplemented type: N" elsewhere. Only add the feature when it's valid.
  if (coordinates.length >= 2) {
    features.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates },
      properties: {
        length_km: trip && trip.summary ? trip.summary.length : null,
        time_s: trip && trip.summary ? trip.summary.time : null,
      },
    });
  }

  return { type: 'FeatureCollection', features };
}

/**
 * Throw a readable Error if a backend/Valhalla response is not usable.
 *
 * The Go layer returns `{ "ERROR": "..." }` or `{ "error": ..., "raw": ... }`
 * on failure, and Valhalla itself returns `{ "error": "...", "status_code": N,
 * "error_code": N }` (e.g. "Unimplemented type: 4" when a matrix/route gets a
 * location shape it can't process). In all these cases the payload is NOT
 * GeoJSON, so handing it to `source.setData()` throws an opaque MapLibre error.
 * Catch it here with a useful message instead.
 *
 * @param {object} response - parsed JSON response
 * @param {string} expectKey - a key that must exist on a successful response
 *                             ('trip' for routes, 'sources_to_targets' for matrix)
 */
export function assertValhallaOk(response, expectKey) {
  if (!response || typeof response !== 'object') {
    throw new Error('Empty or invalid response from routing service.');
  }
  // Backend (Gin) error envelopes
  if (response.ERROR) throw new Error(String(response.ERROR));
  if (response.error) {
    const code =
      response.error_code != null ? ` (code ${response.error_code})` : '';
    throw new Error(`${response.error}${code}`);
  }
  if (expectKey && response[expectKey] == null) {
    throw new Error(`Unexpected response: missing "${expectKey}".`);
  }
}

/* ============================================================================
 * Marker helpers (maplibregl.Marker)
 * ==========================================================================*/

/**
 * Build a circular DOM element used as a custom marker.
 *
 * Using maplibregl.Marker with a custom `element` (per MarkerOptions) renders
 * the marker as an HTML node, so any text/number inside it is plain HTML/CSS —
 * it does NOT depend on the map style's glyph fonts. This avoids the symbol
 * layer "missing font -> nothing renders" problem entirely.
 *
 * @param {object} opts
 * @param {string} opts.label   - text shown inside the circle (e.g. "1")
 * @param {string} opts.bg      - background color
 * @param {number} opts.size    - diameter in px
 * @param {string} [opts.className] - extra CSS class
 * @returns {HTMLElement}
 */
function createMarkerElement({ label = '', bg = '#4aa3ff', size = 26, className = '' }) {
  const el = document.createElement('div');
  el.className = `geocoder-marker ${className}`.trim();
  el.style.width = `${size}px`;
  el.style.height = `${size}px`;
  el.style.background = bg;
  el.style.lineHeight = `${size}px`;
  if (label !== '' && label != null) el.textContent = String(label);
  return el;
}

/**
 * Create a maplibregl.Marker at a {lat, lon} point with a numbered element.
 * Uses the documented MarkerOptions: `element` and `anchor: 'center'`.
 */
function createNumberedMarker(map, { lat, lon }, { label, bg, size, className }) {
  const element = createMarkerElement({ label, bg, size, className });
  return new maplibregl.Marker({ element, anchor: 'center' })
    .setLngLat([lon, lat])
    .addTo(map);
}

/* ============================================================================
 * Shared activation registry
 * ==========================================================================*/

/**
 * All interactive controls register here. When one becomes active it asks the
 * manager to deactivate every other registered control, guaranteeing only a
 * single map-click "mode" is ever live. Stored on the map so multiple maps
 * stay isolated.
 */
class InteractionManager {
  constructor() {
    this.controls = new Set();
  }

  register(control) {
    this.controls.add(control);
  }

  unregister(control) {
    this.controls.delete(control);
  }

  activateExclusive(except) {
    this.controls.forEach((control) => {
      if (control !== except && control.isActive) {
        control.deactivate();
      }
    });
  }

  /**
   * Wipe every registered control's drawn output (markers, lines, layers).
   * Used by any control's "Clear" button so clearing one clears them all.
   */
  clearAllControls() {
    this.controls.forEach((control) => {
      if (typeof control.clearAll === 'function') {
        control.clearAll();
      }
    });
  }
}

function getInteractionManager(map) {
  if (!map._geocoderInteractionManager) {
    map._geocoderInteractionManager = new InteractionManager();
    attachGeocoderErrorDiagnostics(map);
  }
  return map._geocoderInteractionManager;
}

/**
 * Install a one-time error listener that names the source responsible for an
 * async worker error such as "Unimplemented type: N".
 *
 * That error is thrown inside MapLibre's GeoJSON/vector-tile worker while
 * (re)parsing a tile, so it surfaces on whatever operation happens to be in
 * flight — frequently an unrelated `setData` call — which makes it look like
 * the wrong source is to blame. This listener logs `e.sourceId` (and the error)
 * so you can see which source actually received bad data.
 */
function attachGeocoderErrorDiagnostics(map) {
  if (map._geocoderErrorDiag) return;
  map._geocoderErrorDiag = true;
  map.on('error', (e) => {
    const src = e && e.sourceId ? ` [source: ${e.sourceId}]` : '';
    // eslint-disable-next-line no-console
    console.error(`MapLibre error${src}:`, (e && e.error) || e);
  });
}

/* ============================================================================
 * Base controls
 * ==========================================================================*/

/**
 * Base MapLibre Control Interface
 * Implements the required IControl interface methods.
 */
export default class BaseControl {
  constructor(className = '') {
    this.className = className;
  }

  onAdd(map) {
    this._map = map;
    this._container = document.createElement('div');
    this._container.className = `maplibregl-ctrl maplibregl-ctrl-group ${this.className}`;
    this.setupUI();
    return this._container;
  }

  onRemove() {
    if (this._container && this._container.parentNode) {
      this._container.parentNode.removeChild(this._container);
    }
    this._map = undefined;
  }

  setupUI() {
    console.warn('setupUI() should be implemented by the child class.');
  }

  /** Standard MapLibre control button with an icon span. */
  createButton(iconClass, title) {
    const button = document.createElement('button');
    button.type = 'button';
    button.title = title;
    button.setAttribute('aria-label', title);

    const icon = document.createElement('span');
    icon.className = `maplibregl-ctrl-icon icons ${iconClass}`;
    icon.setAttribute('aria-hidden', 'true');

    button.appendChild(icon);
    return button;
  }
}

/**
 * Base for controls that toggle an interactive "click on map" mode. Handles
 * registration with the InteractionManager and the shared active-state
 * bookkeeping so only one interactive control can be active at a time.
 */
class InteractiveControl extends BaseControl {
  constructor(className, defaultSpeed = 120) {
    super(className);
    this.defaultSpeed = defaultSpeed;
    this.isActive = false;
  }

  onAdd(map) {
    const container = super.onAdd(map);
    getInteractionManager(map).register(this);
    return container;
  }

  onRemove() {
    if (this.isActive) this.deactivate();
    if (this._map) getInteractionManager(this._map).unregister(this);
    super.onRemove();
  }

  toggle() {
    if (this.isActive) this.deactivate();
    else this.activate();
  }

  activate() {
    if (this.isActive) return;
    getInteractionManager(this._map).activateExclusive(this);
    this.isActive = true;
    this.onActivate();
  }

  deactivate() {
    if (!this.isActive) return;
    this.isActive = false;
    this.onDeactivate();
  }

  /** Clear this control AND every other geocoder control on the map. */
  clearEverything() {
    if (this._map) getInteractionManager(this._map).clearAllControls();
  }

  onActivate() {}
  onDeactivate() {}
}

/* ============================================================================
 * Speed configuration (non-interactive)
 * ==========================================================================*/

/**
 * Control for Speed Configuration.
 *
 * Sets the top speed used for routing, reachability and the matrix. Compact by
 * default: a single icon button shows the current congestion level; clicking it
 * reveals a small panel of speed options rendered as congestion bars + short
 * "km/h" text instead of a wide dropdown.
 *
 * Speed -> congestion mapping (faster = freer flow):
 *   30  -> heavy   (1 bar)
 *   60  -> heavy   (1 bar)
 *   90  -> medium  (2 bars)
 *   120 -> free    (3 bars)
 *   140 -> free    (3 bars)
 *   ∞   -> free    (3 bars)
 */
export class SpeedConfigurationControl extends BaseControl {
  constructor(defaultSpeed = 'Infinite') {
    super('geocoder-speed-ctrl');
    this.currentSpeed = defaultSpeed === 'Infinite' ? 9999 : defaultSpeed;
    this._open = false;
    // [value, short label, congestion icon class]
    this.options = [
      [30, '30', 'icon-low'],
      [60, '60', 'icon-low'],
      [90, '90', 'icon-medium'],
      [120, '120', 'icon-none'],
      [140, '140', 'icon-none'],
      [9999, '∞', 'icon-none'],
    ];
  }

  congestionIconFor(value) {
    const opt = this.options.find((o) => o[0] === value);
    return opt ? opt[2] : 'icon-none';
  }

  shortLabelFor(value) {
    const opt = this.options.find((o) => o[0] === value);
    return opt ? opt[1] : String(value);
  }

  setupUI() {
    // Compact toggle button showing the current congestion level.
    this.toggleBtn = document.createElement('button');
    this.toggleBtn.type = 'button';
    this.toggleBtn.title = 'Top Speed (km/h)';
    this.toggleBtn.setAttribute('aria-label', 'Top speed');
    this.toggleBtn.className = 'geocoder-speed-toggle';

    this.toggleIcon = document.createElement('span');
    this.toggleIcon.className = `icons ${this.congestionIconFor(this.currentSpeed)}`;
    this.toggleLabel = document.createElement('span');
    this.toggleLabel.className = 'geocoder-speed-current';
    this.toggleLabel.innerText = this.shortLabelFor(this.currentSpeed);

    this.toggleBtn.appendChild(this.toggleIcon);
    this.toggleBtn.appendChild(this.toggleLabel);
    this.toggleBtn.onclick = () => this.togglePanel();
    this._container.appendChild(this.toggleBtn);

    // Options panel (hidden until opened).
    this.panel = document.createElement('div');
    this.panel.className = 'geocoder-speed-panel';
    this.panel.style.display = 'none';

    this.options.forEach(([value, label, iconClass]) => {
      const opt = document.createElement('button');
      opt.type = 'button';
      opt.className = 'geocoder-speed-option';
      if (value === this.currentSpeed) opt.classList.add('selected');
      opt.title = value === 9999 ? 'No speed limit' : `${label} km/h`;

      const bars = document.createElement('span');
      bars.className = `icons ${iconClass}`;
      const txt = document.createElement('span');
      txt.className = 'geocoder-speed-option-label';
      txt.innerText = value === 9999 ? '∞' : `${label}`;

      opt.appendChild(bars);
      opt.appendChild(txt);
      opt.onclick = () => this.selectSpeed(value);
      this.panel.appendChild(opt);
    });

    this._container.appendChild(this.panel);
  }

  togglePanel() {
    this._open = !this._open;
    this.panel.style.display = this._open ? 'flex' : 'none';
    this.toggleBtn.classList.toggle('active', this._open);
  }

  selectSpeed(value) {
    this.currentSpeed = value;
    this._map.geocoderTopSpeed = value;

    // Update the compact button to reflect the new congestion level.
    this.toggleIcon.className = `icons ${this.congestionIconFor(value)}`;
    this.toggleLabel.innerText = this.shortLabelFor(value);

    // Update selected state in the panel.
    Array.from(this.panel.children).forEach((child, i) => {
      child.classList.toggle('selected', this.options[i][0] === value);
    });

    // Close after choosing.
    this.togglePanel();
  }

  onAdd(map) {
    const container = super.onAdd(map);
    this._map.geocoderTopSpeed = this.currentSpeed;
    return container;
  }
}

/* ============================================================================
 * Reachability (isochrone) - interactive
 * ==========================================================================*/

/**
 * Control for Reachability Polygons (Isochrones) - Interactive Click Version.
 * time_to_center already returns GeoJSON (Valhalla isochrone with polygons:true),
 * so no decoding is needed here.
 */
export class ReachabilityControl extends InteractiveControl {
  constructor(defaultSpeed = 120) {
    super('geocoder-reachability-ctrl', defaultSpeed);
    this._boundHandleMapClick = this.handleMapClick.bind(this);
  }

  setupUI() {
    this.button = this.createButton(
      'icon-timecenter',
      'Toggle Reachability Mode (click map to calculate)'
    );
    this.button.onclick = () => this.toggle();
    this._container.appendChild(this.button);
  }

  onActivate() {
    this.button.classList.add('active');
    this._map.getCanvas().style.cursor = 'crosshair';
    this._map.on('click', this._boundHandleMapClick);
  }

  onDeactivate() {
    this.button.classList.remove('active');
    this._map.getCanvas().style.cursor = '';
    this._map.off('click', this._boundHandleMapClick);
    this.clearReachabilityLayers();
  }

  clearReachabilityLayers() {
    const sourceId = 'reachability-source';
    const layerId = 'reachability-fill-layer';
    const outlineLayerId = 'reachability-outline-layer';
    if (this._map.getLayer(outlineLayerId)) this._map.removeLayer(outlineLayerId);
    if (this._map.getLayer(layerId)) this._map.removeLayer(layerId);
    if (this._map.getSource(sourceId)) this._map.removeSource(sourceId);
  }

  // Alias so the InteractionManager's clearAllControls() reaches this control.
  clearAll() {
    this.clearReachabilityLayers();
  }

  async handleMapClick(e) {
    const clickedLat = e.lngLat.lat;
    const clickedLng = e.lngLat.lng;

    try {
      this._map.getCanvas().style.cursor = 'wait';
      const currentSpeed = this._map.geocoderTopSpeed || this.defaultSpeed;

      const geojsonData = await GeoCoderAPIs.request_time_to_center_data(
        clickedLat,
        clickedLng,
        currentSpeed
      );

      const sourceId = 'reachability-source';
      const layerId = 'reachability-fill-layer';
      const outlineLayerId = 'reachability-outline-layer';

      if (this._map.getSource(sourceId)) {
        this._map.getSource(sourceId).setData(geojsonData);
      } else {
        this._map.addSource(sourceId, { type: 'geojson', data: geojsonData });
        this._map.addLayer({
          id: layerId,
          type: 'fill',
          source: sourceId,
          paint: { 'fill-color': '#007bff', 'fill-opacity': 0.2 },
        });
        this._map.addLayer({
          id: outlineLayerId,
          type: 'line',
          source: sourceId,
          paint: { 'line-color': '#0056b3', 'line-width': 2 },
        });
      }
    } catch (error) {
      console.error('Reachability API Error:', error);
      alert('Failed to load reachability data. Check console for details.');
    } finally {
      if (this.isActive) this._map.getCanvas().style.cursor = 'crosshair';
    }
  }
}

/* ============================================================================
 * Routing (route / optimized_route) - interactive
 * ==========================================================================*/

/**
 * Control for Routing - Interactive Click Version.
 *
 * Supports two modes via the `optimize` flag (toggled in the UI):
 *   - optimize = false -> request_route_locations     (Valhalla "route")
 *   - optimize = true  -> request_op_route_locations  (Valhalla "optimized_route")
 *
 * Both endpoints return a Valhalla trip with encoded polyline `shape` per leg,
 * which is decoded (precision 6) into GeoJSON before rendering.
 *
 * @param {object} options
 * @param {number} options.defaultSpeed
 * @param {boolean} options.optimize - initial state of the optimize flag
 */
export class RoutingControl extends InteractiveControl {
  constructor({ defaultSpeed = 120, optimize = false } = {}) {
    super('geocoder-routing-ctrl', defaultSpeed);
    this.optimize = optimize;
    this.waypoints = [];
    this._markers = []; // maplibregl.Marker instances for each waypoint
    this._boundAddWaypoint = this.addWaypoint.bind(this);
  }

  setupUI() {
    this.toggleBtn = this.createButton(
      'icon-route',
      'Toggle Routing Mode (click map to add stops)'
    );
    this.toggleBtn.onclick = () => this.toggle();
    this._container.appendChild(this.toggleBtn);
  }

  onActivate() {
    this.toggleBtn.classList.add('active');
    this._map.getCanvas().style.cursor = 'crosshair';
    this._map.on('click', this._boundAddWaypoint);
  }

  onDeactivate() {
    this.toggleBtn.classList.remove('active');
    this._map.getCanvas().style.cursor = '';
    this._map.off('click', this._boundAddWaypoint);
    this.clearAll()
  }

  addWaypoint(e) {
    this.waypoints.push({ lat: e.lngLat.lat, lon: e.lngLat.lng });
    if (this.waypoints.length >= 2) this.calculateRoute();
    this.drawWaypoints();
  }

  drawWaypoints() {
    // Render each waypoint as a numbered maplibregl.Marker (custom DOM element).
    // Markers are HTML, so the numbers always render regardless of map fonts.
    // Simplest robust approach: clear existing markers and rebuild so the
    // numbering always matches the current order.
    this._markers.forEach((m) => m.remove());
    this._markers = [];

    this.waypoints
      .filter((wp) => wp && Number.isFinite(wp.lon) && Number.isFinite(wp.lat))
      .forEach((wp, i) => {
        const marker = createNumberedMarker(this._map, wp, {
          label: i + 1,
          bg: '#4aa3ff', // lightish blue
          size: 26,
          className: 'geocoder-marker-waypoint',
        });
        this._markers.push(marker);
      });
  }

  async calculateRoute() {
    if (this.waypoints.length < 2) return;

    try {
      const currentSpeed = this._map.geocoderTopSpeed || this.defaultSpeed;

      // Choose endpoint based on the optimize flag.
      const response = this.optimize
        ? await GeoCoderAPIs.request_op_route_locations(this.waypoints, currentSpeed)
        : await GeoCoderAPIs.request_route_locations(this.waypoints, currentSpeed);

      // Surface Valhalla / backend errors instead of feeding them to MapLibre.
      assertValhallaOk(response, 'trip');

      // Valhalla returns encoded polylines per leg -> decode to GeoJSON.
      const geojson = valhallaRouteToGeoJSON(response);

      const sourceId = 'routing-line-source';
      if (this._map.getSource(sourceId)) {
        this._map.getSource(sourceId).setData(geojson);
      } else {
        this._map.addSource(sourceId, { type: 'geojson', data: geojson });
        // Waypoint markers are DOM elements (always above the canvas), so the
        // line can simply be added on top of the other map layers.
        this._map.addLayer({
          id: 'routing-line-layer',
          type: 'line',
          source: sourceId,
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: { 'line-color': '#007bff58', 'line-width': 4 },
        });
      }

    } catch (error) {
      console.error('Routing API Error:', error);
    }
  }

  clearAll() {
    this.waypoints = [];
    // Remove waypoint markers (DOM elements).
    this._markers.forEach((m) => m.remove());
    this._markers = [];

    if (this._map.getLayer('routing-line-layer')) this._map.removeLayer('routing-line-layer');
    if (this._map.getSource('routing-line-source')) this._map.removeSource('routing-line-source');
  }

  onRemove() {
    this.clearAll();
    super.onRemove();
  }
}

// Backwards-compatible alias: old code imported OptimizedRoutingControl.
export class OptimizedRoutingControl extends RoutingControl {
  constructor(defaultSpeed = 120) {
    super({ defaultSpeed, optimize: true });
  }
}

/* ============================================================================
 * Travel-Time Matrix (sources_to_targets) - interactive
 * ==========================================================================*/

/**
 * TravelTimeMatrixControl ("Source -> Destinations" matrix).
 *
 * Workflow:
 *   1. Activate the control.
 *   2. First map click sets the single SOURCE (green marker).
 *   3. Subsequent clicks add DESTINATIONS (blue markers).
 *   4. "Compute" calls source_to_list, which the Go backend maps to Valhalla's
 *      `sources_to_targets`. That returns a distance/time MATRIX (no geometry),
 *      so we render markers labelled with the time (and distance) to each
 *      destination rather than drawing a line.
 *
 * Valhalla `sources_to_targets` response shape:
 *   { sources_to_targets: [ [ {to_index, from_index, time, distance}, ... ] ],
 *     units: "kilometers" }
 */
export class TravelTimeMatrixControl extends InteractiveControl {
  constructor(defaultSpeed = 120) {
    super('geocoder-matrix-ctrl', defaultSpeed);
    this.source = null;
    this.destinations = [];
    this._sourceMarker = null; // maplibregl.Marker for the source
    this._destMarkers = []; // maplibregl.Marker[] for destinations
    this._boundAddPoint = this.addPoint.bind(this);
  }

  setupUI() {
    this.toggleBtn = this.createButton(
      'icon-source-list',
      'Toggle Matrix Mode (click to set source, then destinations)'
    );
    this.toggleBtn.onclick = () => this.toggle();
    this._container.appendChild(this.toggleBtn);
  }

  onActivate() {
    this.toggleBtn.classList.add('active');
    this._map.getCanvas().style.cursor = 'crosshair';
    this._map.on('click', this._boundAddPoint);
  }

  onDeactivate() {
    this.toggleBtn.classList.remove('active');
    this._map.getCanvas().style.cursor = '';
    this._map.off('click', this._boundAddPoint);
    this.clearAll();
  }

  addPoint(e) {
    const pt = { lat: e.lngLat.lat, lon: e.lngLat.lng };

    if (!this.source) {
      this.source = pt;
    } else {
      this.destinations.push(pt);
    }
    this.computeMatrix();
  }

  drawPoints() {
    // Source marker (green). Rebuild it each time so position stays in sync.
    if (this._sourceMarker) {
      this._sourceMarker.remove();
      this._sourceMarker = null;
    }
    if (this.source && Number.isFinite(this.source.lon) && Number.isFinite(this.source.lat)) {
      this._sourceMarker = createNumberedMarker(this._map, this.source, {
        label: '',
        bg: '#2ecc71', // green = source
        size: 26,
        className: 'geocoder-marker-source',
      });
    }

    // Destination markers (blue). Label shows the index until a matrix is
    // computed, then the travel time (set on d.label by renderResults).
    this._destMarkers.forEach((m) => m.remove());
    this._destMarkers = [];

    this.destinations
      .filter((d) => d && Number.isFinite(d.lon) && Number.isFinite(d.lat))
      .forEach((d, i) => {
        const marker = createNumberedMarker(this._map, d, {
          label: d.label || `${i + 1}`,
          bg: '#3b82f6', // blue = destination
          size: 26,
          className: 'geocoder-marker-dest',
        });
        this._destMarkers.push(marker);
      });
  }

  async computeMatrix() {
    if (!this.source || this.destinations.length < 1) return this.drawPoints();

    try {
      const currentSpeed = this._map.geocoderTopSpeed || this.defaultSpeed;
      console.log("this.destinations",this.destinations,"this.source",this.source)
      // One source, many destinations.
      const response = await GeoCoderAPIs.request_source_to_list(
        [this.source],
        this.destinations.map(({ lat, lon }) => ({ lat, lon })),//remove "label" attribute
        currentSpeed
      );

      // Surface Valhalla / backend errors (e.g. "Unimplemented type: 4").
      assertValhallaOk(response, 'sources_to_targets');

      this.renderResults(response);
    } catch (error) {
      throw error;
    } finally {
    }
  }

  /**
   * Parse the Valhalla sources_to_targets matrix and annotate destinations.
   * With a single source, row 0 holds one entry per destination.
   */
  renderResults(response) {
    const matrix = response && response.sources_to_targets;
    const units = (response && response.units) || 'km';
    const row = Array.isArray(matrix) && matrix.length ? matrix[0] : [];

    row.forEach((cell, i) => {
      const minutes =
        cell && typeof cell.time === 'number' ? Math.round(cell.time / 60) : null;
      const dist =
        cell && typeof cell.distance === 'number' ? cell.distance.toFixed(1) : null;

      // Annotate the marker label with the computed time.
      if (this.destinations[i]) {
        this.destinations[i].label =
          minutes !== null ? `${minutes}min (${dist})` : `#${i + 1}`;
      }
    });

    this.drawPoints(); // refresh labels with times
  }

  clearAll() {
    this.source = null;
    this.destinations = [];
    // Remove all markers (DOM elements).
    if (this._sourceMarker) {
      this._sourceMarker.remove();
      this._sourceMarker = null;
    }
    this._destMarkers.forEach((m) => m.remove());
    this._destMarkers = [];
  }

  onRemove() {
    this.clearAll();
    super.onRemove();
  }
}